import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  platformApi,
  type PlatformAnnouncement,
  type PlatformAnnouncementCategory,
  type SendPlatformAnnouncementPayload,
} from '@/core/api/platformApi';
import { getErrorMessage } from '@/core/utils/errors';
import { AppIcon } from '@/shared/components/AppIcon';

const formatDate = (value?: string | null): string => {
  if (!value) return '—';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? '—'
    : new Intl.DateTimeFormat('fa-IR-u-ca-persian', {
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(parsed);
};

const categories: Array<{
  value: PlatformAnnouncementCategory;
  label: string;
  description: string;
  className: string;
}> = [
  { value: 'information', label: 'اطلاع‌رسانی', description: 'پیام عمومی و خبری', className: 'border-sky-200 bg-sky-50 text-sky-700' },
  { value: 'warning', label: 'هشدار', description: 'موضوعی که نیاز به توجه دارد', className: 'border-amber-200 bg-amber-50 text-amber-700' },
  { value: 'critical', label: 'فوری', description: 'موضوع مهم و فوری', className: 'border-rose-200 bg-rose-50 text-rose-700' },
];

const categoryInfo = (value: PlatformAnnouncement['category']) =>
  categories.find((item) => item.value === value) ?? categories[0];

const isActiveTenant = (status: string): boolean => status.toLowerCase() === 'active';

export const PlatformAnnouncementsPage = (): React.ReactElement => {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [category, setCategory] = useState<PlatformAnnouncementCategory>('information');
  const [sendToAll, setSendToAll] = useState(false);
  const [selectedTenantIds, setSelectedTenantIds] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const tenantsQuery = useQuery({
    queryKey: ['platform', 'tenants', 'announcement-targets'],
    queryFn: platformApi.list,
  });
  const historyQuery = useQuery({
    queryKey: ['platform', 'announcements'],
    queryFn: () => platformApi.listAnnouncements(300),
  });

  const activeTenants = useMemo(
    () => (tenantsQuery.data ?? []).filter((tenant) => isActiveTenant(tenant.status)),
    [tenantsQuery.data],
  );
  const visibleTenants = useMemo(() => {
    const term = search.trim().toLocaleLowerCase('fa');
    if (!term) return activeTenants;
    return activeTenants.filter(
      (tenant) => tenant.name.toLocaleLowerCase('fa').includes(term) || tenant.code.toLocaleLowerCase('en').includes(term),
    );
  }, [activeTenants, search]);

  const sendMutation = useMutation({
    mutationFn: (payload: SendPlatformAnnouncementPayload) => platformApi.sendAnnouncement(payload),
    onSuccess: async (result) => {
      setFeedback({ type: 'success', text: `پیام با موفقیت برای ${result.recipientCount.toLocaleString('fa-IR')} شرکت ارسال شد.` });
      setTitle('');
      setMessage('');
      setCategory('information');
      setSendToAll(false);
      setSelectedTenantIds([]);
      await queryClient.invalidateQueries({ queryKey: ['platform', 'announcements'] });
    },
    onError: (error) => setFeedback({ type: 'error', text: getErrorMessage(error) }),
  });

  const toggleTenant = (tenantId: string): void => {
    setSelectedTenantIds((current) =>
      current.includes(tenantId) ? current.filter((id) => id !== tenantId) : [...current, tenantId],
    );
  };

  const submit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setFeedback(null);
    if (!title.trim()) {
      setFeedback({ type: 'error', text: 'عنوان پیام را وارد کنید.' });
      return;
    }
    if (!message.trim()) {
      setFeedback({ type: 'error', text: 'متن پیام را وارد کنید.' });
      return;
    }
    if (!sendToAll && selectedTenantIds.length === 0) {
      setFeedback({ type: 'error', text: 'حداقل یک شرکت را انتخاب کنید.' });
      return;
    }
    sendMutation.mutate({
      title: title.trim(),
      message: message.trim(),
      category,
      sendToAllActiveTenants: sendToAll,
      tenantIds: sendToAll ? [] : selectedTenantIds,
    });
  };

  const loadError = tenantsQuery.error ?? historyQuery.error;

  return (
    <div className="min-h-[calc(100vh-74px)] text-slate-900" dir="rtl">
      <main className="mx-auto max-w-[1500px] space-y-6 px-4 py-6 sm:px-7 lg:py-8">
        <section className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm shadow-slate-950/5">
          <div className="border-b border-slate-100 bg-gradient-to-l from-indigo-50 via-white to-sky-50 px-5 py-6 sm:px-8">
            <div className="flex flex-col justify-between gap-4 lg:flex-row lg:items-center">
              <div>
                <p className="mb-2 text-xs font-black text-indigo-600">ارتباط مستقیم با شرکت‌ها</p>
                <h1 className="text-xl font-black text-slate-950 sm:text-2xl">ارسال اطلاعیه از طرف مدیر پلتفرم</h1>
                <p className="mt-2 max-w-3xl text-sm leading-7 text-slate-500">
                  پیام داخل پنل شرکت نمایش داده می‌شود و اولین مشاهده، همراه با نام مشاهده‌کننده و تاریخ شمسی ثبت خواهد شد.
                </p>
              </div>
              <div className="grid w-full shrink-0 grid-cols-2 gap-3 sm:w-auto">
                <div className="rounded-2xl border border-white bg-white/80 px-4 py-3 text-center shadow-sm">
                  <p className="text-[11px] font-bold text-slate-400">شرکت فعال</p>
                  <p className="mt-1 text-lg font-black text-slate-950">{activeTenants.length.toLocaleString('fa-IR')}</p>
                </div>
                <div className="rounded-2xl border border-white bg-white/80 px-4 py-3 text-center shadow-sm">
                  <p className="text-[11px] font-bold text-slate-400">پیام خوانده‌نشده</p>
                  <p className="mt-1 text-lg font-black text-amber-600">
                    {(historyQuery.data?.filter((item) => !item.isViewed).length ?? 0).toLocaleString('fa-IR')}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <form className="grid gap-6 p-5 sm:p-8 xl:grid-cols-[minmax(0,1fr)_minmax(360px,0.75fr)]" onSubmit={submit}>
            <div className="space-y-5">
              <label className="block">
                <span className="mb-2 block text-sm font-black text-slate-700">عنوان پیام</span>
                <input
                  className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 text-sm font-bold outline-none transition placeholder:text-slate-400 focus:border-indigo-400 focus:bg-white focus:ring-4 focus:ring-indigo-100"
                  maxLength={200}
                  onChange={(event) => setTitle(event.target.value)}
                  placeholder="مثلاً بروزرسانی سرویس در روز جمعه"
                  value={title}
                />
                <span className="mt-1 block text-left text-[11px] text-slate-400">{title.length.toLocaleString('fa-IR')} / ۲۰۰</span>
              </label>

              <label className="block">
                <span className="mb-2 block text-sm font-black text-slate-700">متن پیام</span>
                <textarea
                  className="min-h-40 w-full resize-y rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm leading-7 outline-none transition placeholder:text-slate-400 focus:border-indigo-400 focus:bg-white focus:ring-4 focus:ring-indigo-100"
                  maxLength={4000}
                  onChange={(event) => setMessage(event.target.value)}
                  placeholder="متن کامل اطلاعیه را بنویسید…"
                  value={message}
                />
                <span className="mt-1 block text-left text-[11px] text-slate-400">{message.length.toLocaleString('fa-IR')} / ۴٬۰۰۰</span>
              </label>

              <fieldset>
                <legend className="mb-2 text-sm font-black text-slate-700">اهمیت پیام</legend>
                <div className="grid gap-2 sm:grid-cols-3">
                  {categories.map((item) => (
                    <label
                      className={`cursor-pointer rounded-2xl border p-3 transition ${category === item.value ? item.className : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'}`}
                      key={item.value}
                    >
                      <input className="sr-only" checked={category === item.value} name="category" onChange={() => setCategory(item.value)} type="radio" />
                      <span className="block text-sm font-black">{item.label}</span>
                      <span className="mt-1 block text-[11px] opacity-75">{item.description}</span>
                    </label>
                  ))}
                </div>
              </fieldset>
            </div>

            <div className="flex min-h-0 flex-col rounded-3xl border border-slate-200 bg-slate-50 p-4 sm:p-5">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h2 className="text-sm font-black text-slate-800">گیرندگان پیام</h2>
                  <p className="mt-1 text-xs text-slate-400">شرکت‌های فعال قابل انتخاب هستند.</p>
                </div>
                <span className="rounded-full bg-indigo-100 px-3 py-1 text-xs font-black text-indigo-700">
                  {(sendToAll ? activeTenants.length : selectedTenantIds.length).toLocaleString('fa-IR')} گیرنده
                </span>
              </div>

              <label className="mt-4 flex cursor-pointer items-center gap-3 rounded-2xl border border-indigo-200 bg-indigo-50 p-3 text-sm font-black text-indigo-800">
                <input
                  className="size-4 accent-indigo-600"
                  checked={sendToAll}
                  onChange={(event) => setSendToAll(event.target.checked)}
                  type="checkbox"
                />
                ارسال به همه شرکت‌های فعال
              </label>

              {!sendToAll ? (
                <>
                  <label className="mt-3 flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-slate-400">
                    <AppIcon name="search" size={17} />
                    <input
                      className="min-w-0 flex-1 bg-transparent text-sm text-slate-700 outline-none"
                      onChange={(event) => setSearch(event.target.value)}
                      placeholder="جست‌وجوی نام یا کد سازمانی"
                      value={search}
                    />
                  </label>
                  <div className="mt-3 max-h-64 space-y-2 overflow-y-auto pl-1">
                    {tenantsQuery.isLoading ? <p className="py-8 text-center text-sm text-slate-400">در حال دریافت شرکت‌ها…</p> : null}
                    {!tenantsQuery.isLoading && visibleTenants.length === 0 ? <p className="py-8 text-center text-sm text-slate-400">شرکت فعالی پیدا نشد.</p> : null}
                    {visibleTenants.map((tenant) => {
                      const selected = selectedTenantIds.includes(tenant.id);
                      return (
                        <button
                          className={`flex w-full items-center gap-3 rounded-2xl border p-3 text-right transition ${selected ? 'border-indigo-300 bg-indigo-50' : 'border-slate-200 bg-white hover:border-slate-300'}`}
                          key={tenant.id}
                          onClick={() => toggleTenant(tenant.id)}
                          type="button"
                        >
                          <span className={`grid size-5 shrink-0 place-items-center rounded-md border ${selected ? 'border-indigo-600 bg-indigo-600 text-white' : 'border-slate-300 bg-white text-transparent'}`}>
                            <AppIcon name="check" size={13} />
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-black text-slate-800">{tenant.name}</span>
                            <span className="mt-0.5 block truncate text-[11px] text-slate-400">کد سازمانی: {tenant.code}</span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </>
              ) : (
                <div className="mt-3 grid flex-1 place-items-center rounded-2xl border border-dashed border-indigo-200 bg-white p-6 text-center">
                  <div>
                    <span className="mx-auto grid size-12 place-items-center rounded-2xl bg-indigo-100 text-indigo-600"><AppIcon name="building" /></span>
                    <p className="mt-3 text-sm font-black text-slate-700">همه شرکت‌های فعال</p>
                    <p className="mt-1 text-xs leading-6 text-slate-400">در زمان ارسال، فهرست شرکت‌های فعال دوباره کنترل می‌شود.</p>
                  </div>
                </div>
              )}

              <button
                className="mt-4 inline-flex h-12 w-full items-center justify-center gap-2 rounded-2xl bg-slate-950 px-5 text-sm font-black text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={sendMutation.isPending || tenantsQuery.isLoading}
                type="submit"
              >
                <AppIcon name="send" size={18} />
                {sendMutation.isPending ? 'در حال ارسال…' : 'ارسال پیام به شرکت‌ها'}
              </button>
            </div>
          </form>

          {feedback ? (
            <div className={`mx-5 mb-6 rounded-2xl border px-4 py-3 text-sm font-bold sm:mx-8 ${feedback.type === 'success' ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-rose-200 bg-rose-50 text-rose-700'}`}>
              {feedback.text}
            </div>
          ) : null}
          {loadError ? <div className="mx-5 mb-6 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-bold text-rose-700 sm:mx-8">{getErrorMessage(loadError)}</div> : null}
        </section>

        <section className="rounded-[2rem] border border-slate-200 bg-white p-5 shadow-sm shadow-slate-950/5 sm:p-8">
          <div className="mb-5 flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
            <div>
              <h2 className="text-lg font-black text-slate-950">تاریخچه ارسال و مشاهده</h2>
              <p className="mt-1 text-xs leading-6 text-slate-400">هر ردیف، وضعیت پیام برای یک شرکت را نمایش می‌دهد.</p>
            </div>
            <button className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 px-4 py-2 text-xs font-black text-slate-600 hover:bg-slate-50 disabled:opacity-50" disabled={historyQuery.isFetching} onClick={() => void historyQuery.refetch()} type="button">
              <AppIcon name="activity" size={16} />
              {historyQuery.isFetching ? 'در حال بروزرسانی…' : 'بروزرسانی'}
            </button>
          </div>

          {historyQuery.isLoading ? <p className="py-12 text-center text-sm text-slate-400">در حال دریافت تاریخچه…</p> : null}
          {!historyQuery.isLoading && (historyQuery.data?.length ?? 0) === 0 ? (
            <div className="rounded-3xl border border-dashed border-slate-200 py-12 text-center">
              <span className="mx-auto grid size-12 place-items-center rounded-2xl bg-slate-100 text-slate-400"><AppIcon name="bell" /></span>
              <p className="mt-3 text-sm font-black text-slate-600">هنوز پیامی ارسال نشده است.</p>
            </div>
          ) : null}

          <div className="space-y-3 lg:hidden">
            {historyQuery.data?.map((item) => {
              const info = categoryInfo(item.category);
              return (
                <article className="rounded-2xl border border-slate-200 p-4" key={item.id}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0"><p className="truncate text-sm font-black text-slate-900">{item.title}</p><p className="mt-1 truncate text-xs text-slate-500">{item.tenantName ?? 'شرکت حذف‌شده'}</p></div>
                    <span className={`shrink-0 rounded-full border px-2.5 py-1 text-[10px] font-black ${info.className}`}>{info.label}</span>
                  </div>
                  <p className="mt-3 line-clamp-3 whitespace-pre-wrap text-xs leading-6 text-slate-500">{item.message}</p>
                  <div className="mt-3 grid gap-2 border-t border-slate-100 pt-3 text-[11px] text-slate-500 sm:grid-cols-2">
                    <p>ارسال: <span className="font-bold text-slate-700">{formatDate(item.sentAtUtc)}</span></p>
                    <p>فرستنده: <span className="font-bold text-slate-700">{item.sentByDisplayName}</span></p>
                    <p className="sm:col-span-2">مشاهده: {item.isViewed ? <span className="font-bold text-emerald-700">{formatDate(item.viewedAtUtc)} توسط {item.viewedByDisplayName}</span> : <span className="font-bold text-amber-600">خوانده نشده</span>}</p>
                  </div>
                </article>
              );
            })}
          </div>

          {(historyQuery.data?.length ?? 0) > 0 ? (
            <div className="hidden overflow-x-auto rounded-2xl border border-slate-200 lg:block">
              <table className="w-full min-w-[1050px] border-collapse text-right text-xs">
                <thead className="bg-slate-50 text-slate-500"><tr><th className="p-4 font-black">پیام</th><th className="p-4 font-black">شرکت</th><th className="p-4 font-black">فرستنده</th><th className="p-4 font-black">تاریخ ارسال</th><th className="p-4 font-black">وضعیت مشاهده</th><th className="p-4 font-black">مشاهده‌کننده</th></tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {historyQuery.data?.map((item) => {
                    const info = categoryInfo(item.category);
                    return (
                      <tr className="align-top hover:bg-slate-50/60" key={item.id}>
                        <td className="max-w-sm p-4"><div className="flex items-center gap-2"><p className="font-black text-slate-800">{item.title}</p><span className={`rounded-full border px-2 py-0.5 text-[9px] font-black ${info.className}`}>{info.label}</span></div><p className="mt-1 line-clamp-2 whitespace-pre-wrap leading-5 text-slate-400">{item.message}</p></td>
                        <td className="p-4 font-bold text-slate-700">{item.tenantName ?? 'شرکت حذف‌شده'}</td>
                        <td className="p-4 text-slate-600">{item.sentByDisplayName}</td>
                        <td className="whitespace-nowrap p-4 text-slate-600">{formatDate(item.sentAtUtc)}</td>
                        <td className="whitespace-nowrap p-4">{item.isViewed ? <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 font-black text-emerald-700"><AppIcon name="check" size={12} />{formatDate(item.viewedAtUtc)}</span> : <span className="rounded-full bg-amber-50 px-2.5 py-1 font-black text-amber-700">خوانده نشده</span>}</td>
                        <td className="p-4 font-bold text-slate-600">{item.viewedByDisplayName ?? '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : null}
        </section>
      </main>
    </div>
  );
};
